import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { StyledScrollbar } from './style';
import Icon from '../Icon';
import { withMediaQueries } from '../../../app/hoc/withMediaQueries';

const Scrollbar = ({ mediaIsPhone, mediaIsTablet, children, className }) => {
  const contentRef = useRef(null);
  const scrollTrackRef = useRef(null);
  const scrollThumbRef = useRef(null);
  const [thumbWidth, setThumbWidth] = useState(20);
  const [scrollStartPosition, setScrollStartPosition] = useState(null);
  const [initialScrollLeft, setInitialScrollLeft] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  function handleResize() {
    const { clientWidth, scrollWidth } = contentRef.current;
    const { clientWidth: trackSize } = scrollTrackRef.current;
    setThumbWidth(Math.max((clientWidth / scrollWidth) * trackSize, 20));
  }

  const handleTrackClick = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      const { current: trackCurrent } = scrollTrackRef;
      const { current: contentCurrent } = contentRef;
      if (trackCurrent && contentCurrent) {
        const { target, clientX } = e;
        const rect = target.getBoundingClientRect();
        const trackLeft = rect.left;
        const thumbOffset = -(thumbWidth / 2);
        const clickRatio =
          (clientX - trackLeft + thumbOffset) / trackCurrent.clientWidth;
        const scrollAmount = Math.floor(
          clickRatio * contentCurrent.scrollWidth
        );
        contentCurrent.scrollTo({
          left: scrollAmount,
          behavior: 'smooth',
        });
      }
    },
    [thumbWidth]
  );

  const handleThumbPosition = useCallback(() => {
    if (
      !contentRef.current ||
      !scrollTrackRef.current ||
      !scrollThumbRef.current
    ) {
      return;
    }
    const { scrollLeft: contentLeft, scrollWidth: contentWidth } =
      contentRef.current;
    const { clientWidth: trackWidth } = scrollTrackRef.current;
    let newLeft = (+contentLeft / +contentWidth) * trackWidth;
    newLeft = Math.min(newLeft, trackWidth - thumbWidth);
    const thumb = scrollThumbRef.current;
    thumb.style.left = `${newLeft}px`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleThumbMousedown = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setScrollStartPosition(e.clientX);
    if (contentRef.current) setInitialScrollLeft(contentRef.current.scrollLeft);
    setIsDragging(true);
  }, []);

  const handleThumbMouseup = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        setIsDragging(false);
      }
    },
    [isDragging]
  );

  const handleThumbMousemove = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isDragging) {
        const {
          scrollWidth: contentScrollWidth,
          offsetWidth: contentOffsetWidth,
        } = contentRef.current;

        const deltaY =
          (e.clientX - scrollStartPosition) * (contentOffsetWidth / thumbWidth);
        const newScrollLeft = Math.min(
          initialScrollLeft + deltaY,
          contentScrollWidth - contentOffsetWidth
        );

        contentRef.current.scrollLeft = newScrollLeft;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isDragging, scrollStartPosition, thumbWidth]
  );

  // If the content and the scrollbar track exist, use an eventListener to adjust width of thumb and listen for scroll event to move the thumb
  useEffect(() => {
    if (contentRef.current && scrollTrackRef.current) {
      const ref = contentRef.current;
      ref.addEventListener('scroll', handleThumbPosition);
      window.addEventListener('resize', handleResize);
      handleResize();
      return () => {
        ref.removeEventListener('scroll', handleThumbPosition);
        window.removeEventListener('resize', handleResize);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for mouse events to handle scrolling by dragging the thumb
  useEffect(() => {
    document.addEventListener('mousemove', handleThumbMousemove);
    document.addEventListener('mouseup', handleThumbMouseup);
    document.addEventListener('mouseleave', handleThumbMouseup);
    return () => {
      document.removeEventListener('mousemove', handleThumbMousemove);
      document.removeEventListener('mouseup', handleThumbMouseup);
      document.removeEventListener('mouseleave', handleThumbMouseup);
    };
  }, [handleThumbMousemove, handleThumbMouseup]);

  return (
    <StyledScrollbar>
      <div className="custom-scrollbars__container">
        <div className="custom-scrollbars__content" ref={contentRef}>
          {children}
        </div>
        <div className="custom-scrollbars__scrollbar">
          <div className="custom-scrollbars__track-and-thumb">
            <div
              className="custom-scrollbars__track"
              ref={scrollTrackRef}
              onClick={handleTrackClick}
              style={{ cursor: isDragging && 'grabbing' }}
            />
            <div
              className="custom-scrollbars__thumb"
              ref={scrollThumbRef}
              onMouseDown={handleThumbMousedown}
              style={{
                width: `${thumbWidth}px`,
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
            >
              {!(mediaIsPhone || mediaIsTablet) && (
                <Icon type="dots-horizontal" size={25} />
              )}
            </div>
          </div>
        </div>
      </div>
    </StyledScrollbar>
  );
};

Scrollbar.propTypes = {
  mediaIsPhone: PropTypes.bool,
  mediaIsTablet: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
};

export default withMediaQueries(Scrollbar);
